import React, { useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { FiPrinter, FiCheck, FiMinus, FiPlus, FiX } from "react-icons/fi";
import { FaFortAwesome, FaGolfBall } from "react-icons/fa";
import AdminDashboardNav from "../comps/AdminDashboardNav";
import "./PrintPage.css";

// Explicit env var wins (prod, set via Render dashboard). Dev has none set
// (see pws-con/.env), so it falls back to the page's own hostname on port
// 4000 - works for localhost AND a LAN IP (eg http://10.0.0.8:3001/print
// talks to http://10.0.0.8:4000) without hardcoding either one.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || `http://${window.location.hostname}:4000`;

// Same 5 products macro-print.js/backend's /api/print-jobs hardcode - a fixed,
// rarely-changing physical label set, not worth fetching from the products
// table for. Names match exactly what's stored on a ticket row at checkout,
// straight from the products table - that's how a scanned ticket gets mapped
// back to one of these 5 print templates.
const PRODUCTS = [
  { id: "lek30", name: "Lek 30 min", icon: FaFortAwesome, category: "lek" },
  { id: "lek1t", name: "Lek 1 time", icon: FaFortAwesome, category: "lek" },
  { id: "lek2t", name: "Lek 2 timer", icon: FaFortAwesome, category: "lek" },
  { id: "lekdag", name: "Lek Hele Dagen", icon: FaFortAwesome, category: "lek" },
  { id: "golf", name: "Golf 1 Runde", icon: FaGolfBall, category: "golf" },
];

function matchProduct(ticketName) {
  const normalized = String(ticketName || '').trim().toLowerCase();
  return PRODUCTS.find(p => p.name.toLowerCase() === normalized) || null;
}

// navigator.vibrate() (same call my-app's Products.jsx uses) is a silent
// no-op on iOS - WebKit never implemented the Vibration API there. Covers Android.
const vibrate = () => {
  if (navigator.vibrate) navigator.vibrate(30);
};

// The iOS fallback: a native switch-checkbox fires the Taptic Engine, but
// only when a click on it is trusted - a real <label> wrapping the switch
// natively forwards a genuine trusted click to it as a browser default
// action, which is what still works even on iOS 26.5+.
function HapticSwitch(props) {
  return <input type="checkbox" switch="" className="haptic-switch" {...props} />;
}

// The scanner emits US-layout scancodes; a device's OS keyboard set to
// Norwegian (eg the second location's iPad, shared with ExtandaGo POS, can't
// be swapped per-use) translates those scancodes into the WRONG characters
// before the browser ever sees them. This is a deterministic, well-defined
// OS-level keyboard mapping (US key position -> Norwegian layout's character
// at that same physical key), not scanner-specific guesswork:
//   US `{` key position -> NO's Å letter key (shifted)
//   US `"` key position -> NO's Æ letter key (shifted)
//   US `:` key position -> NO's Ø letter key (shifted)
//   US `-` key position -> NO's +  key
//   US `}` key position -> NO's dead-key (circumflex) - nothing follows it to
//     accent (it's the last character of the JSON), so depending on timing
//     it either gets dropped entirely or shows up as a bare trailing `^`.
//     Either way the real `}` never arrives, so it's stripped/re-appended here.
function fixScannerLayoutMismatch(raw) {
  let fixed = raw
    .replace(/Å/g, '{')
    .replace(/Æ/g, '"')
    .replace(/Ø/g, ':')
    .replace(/\+/g, '-')
    .replace(/\^+$/, ''); // bare dead-key leftover, if it rendered at all
  if (fixed.startsWith('{') && !fixed.endsWith('}')) fixed += '}';
  return fixed;
}

const MODAL_SECONDS = 15;

// A manual print has no scanned customer, a scan print always does, so
// customerName is simply omitted when there's none rather than branching.
function PrintModal({ printModal, onClose }) {
  if (!printModal) return null;
  return (
    <div className="print-modal-overlay">
      <div className="print-modal">
        <button type="button" className="print-modal-close" onClick={onClose} aria-label="Lukk">
          <FiX size={22} />
        </button>
        <FiPrinter size={36} />
        <div className="print-modal-title">
          Skriver ut {printModal.tickets.length > 1 ? `${printModal.tickets.length} billetter` : "billett"}
        </div>
        {printModal.tickets.map((ticket) => (
          <div key={ticket.ticketDocRef} className="print-modal-ticket">
            <span className="print-modal-ticket-name">
              {ticket.count > 1 ? `${ticket.count}x ` : ""}{ticket.name}
            </span>
            {ticket.customerName && (
              <span className="print-modal-ticket-customer">{ticket.customerName}</span>
            )}
          </div>
        ))}
        <div className="print-modal-countdown">{printModal.secondsLeft}</div>
      </div>
    </div>
  );
}

export default function PrintPage() {
  // iOS Safari's elastic overscroll (rubber-banding past the top/bottom) reveals
  // whatever's behind the page, which defaults to the html/body's own white
  // background - every other admin page is light-themed so that's set globally,
  // but this one page is dark, so it's set/restored here instead of globally.
  useEffect(() => {
    const { documentElement, body } = document;
    const prevHtmlBg = documentElement.style.background;
    const prevBodyBg = body.style.background;
    documentElement.style.background = "#060a16";
    body.style.background = "#060a16";
    return () => {
      documentElement.style.background = prevHtmlBg;
      body.style.background = prevBodyBg;
    };
  }, []);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@600;700;800&family=Syne:wght@800&display=swap" rel="stylesheet" />
      <Toaster />
      <div className="print-page-shell">
        <AdminDashboardNav />
        <PrintPageContent />
      </div>
    </>
  );
}

function PrintPageContent() {
  const [selectedId, setSelectedId] = useState(null);
  const [qty, setQty] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [printModal, setPrintModal] = useState(null); // { tickets, secondsLeft } | null
  const [scanValue, setScanValue] = useState("");
  const countdownRef = useRef(null);
  const scanInputRef = useRef(null);
  const scanDebounceRef = useRef(null);

  // iOS Safari apparently doesn't dispatch keyboard events to the page's JS
  // at all unless some element actually has focus (unlike desktop, where a
  // document-level keydown listener works with nothing focused - that was
  // tried first and didn't work on the iPad). So this needs a real focused
  // input after all - just hidden via off-screen positioning (real width/
  // height, just placed outside the viewport) rather than the zero-size/
  // opacity-0 trick from before, which iOS seems to refuse to focus at all.
  useEffect(() => {
    scanInputRef.current?.focus();
    return () => clearInterval(countdownRef.current);
  }, []);

  const refocusScanInput = () => {
    // No setTimeout - deferring this breaks out of the calling event's
    // gesture window, which iOS requires .focus() to stay inside.
    scanInputRef.current?.focus();
  };

  const showPrintModal = (tickets) => {
    clearInterval(countdownRef.current);
    setPrintModal({ tickets, secondsLeft: MODAL_SECONDS });
    countdownRef.current = setInterval(() => {
      setPrintModal(prev => {
        if (!prev || prev.secondsLeft <= 1) {
          clearInterval(countdownRef.current);
          return null;
        }
        return { ...prev, secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);
  };

  const handlePrint = async () => {
    if (!selectedId || printing) return;
    vibrate();
    setPrinting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/print-jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: selectedId, count: qty }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Utskrift feilet");
      const product = PRODUCTS.find(p => p.id === selectedId);
      showPrintModal([{ ticketDocRef: "manual-" + Date.now(), name: product.name, count: qty }]);
      setSelectedId(null);
    } catch (error) {
      toast.error(error.message, { duration: 4000, position: "top-center" });
    } finally {
      setPrinting(false);
    }
  };

  const printScannedTicket = async (ticket) => {
    const product = matchProduct(ticket.name);
    if (!product) {
      toast.error(`Ukjent produkttype: "${ticket.name}"`, { duration: 4000, position: "top-center" });
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/print-jobs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId: product.id,
          count: 1,
          ticketDocRef: ticket.ticketDocRef,
          ticketDuration: ticket.duration,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Utskrift feilet");
    } catch (error) {
      toast.error(error.message, { duration: 4000, position: "top-center" });
    }
  };

  // Second-location flow: scanner reads a real ticket QR - same
  // {ticketId}/{linkCode} JSON payload backend/utils/ticketQr.js already puts
  // in every emailed/printed ticket QR. Looked up via the open /scan-lookup
  // endpoint (no KIOSK_API_KEY - same risk posture already accepted for
  // /api/print-jobs on this page). A match prints immediately, no confirm
  // tap - staff already confirmed intent by physically scanning.
  const processScan = async (raw) => {
    raw = raw.trim();
    setScanValue("");
    if (!raw) return;

    let payload;
    try {
      // Try as-is first (US-layout device), fall back to the NO-layout
      // reversal - works regardless of which keyboard layout the scanning
      // device actually has.
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = JSON.parse(fixScannerLayoutMismatch(raw));
      }
      if (parsed.ticketId) payload = { ticketId: parsed.ticketId };
      else if (parsed.linkCode) payload = { linkCode: parsed.linkCode };
      else throw new Error("no ticketId/linkCode in QR");
    } catch {
      toast.error("Kunne ikke lese QR-koden", { duration: 3000, position: "top-center" });
      refocusScanInput();
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/scan-lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Oppslag feilet");
      if (data.tickets.length === 0) {
        toast.error("Fant ingen billett", { duration: 3000, position: "top-center" });
      } else {
        vibrate();
        showPrintModal(data.tickets);
        data.tickets.forEach(printScannedTicket); // fire-and-forget, auto, no confirm step
      }
    } catch (error) {
      toast.error(error.message, { duration: 4000, position: "top-center" });
    } finally {
      refocusScanInput();
    }
  };

  return (
    <div className="print-page" onClick={refocusScanInput}>
      {/* Visually off-screen, not zero-size/opacity - iOS Safari refuses
          focus on a genuinely invisible element, but honors one that's just
          positioned outside the viewport. Background keyboard-emulated input
          from the USB scanner lands here regardless of what's on screen. */}
      <input
        ref={scanInputRef}
        type="text"
        className="scan-input-offscreen"
        value={scanValue}
        onChange={(e) => {
          const value = e.target.value;
          setScanValue(value);
          // Not relying on an Enter/terminator keystroke at all - this
          // scanner's terminator isn't a real "Enter" keydown (neither native
          // form-submit nor an explicit Enter check ever fired, even though
          // the characters themselves reach the input fine). Scanner input
          // arrives in one fast burst, far faster than human typing - a
          // 200ms pause reliably means the scan is done.
          clearTimeout(scanDebounceRef.current);
          scanDebounceRef.current = setTimeout(() => processScan(value), 200);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            clearTimeout(scanDebounceRef.current);
            processScan(scanValue);
          }
        }}
        onBlur={refocusScanInput}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className="print-products">
        {PRODUCTS.map((product) => {
          const Icon = product.icon;
          const selected = selectedId === product.id;
          return (
            <label
              key={product.id}
              className={`ticket-card${selected ? " selected" : ""}`}
            >
              <HapticSwitch
                checked={selected}
                onChange={() => { vibrate(); setSelectedId(product.id); }}
              />
              <div className="card-top-row">
                <div className={`icon-badge icon-badge--${product.category}`}><Icon size={32} /></div>
                <div className="check-badge"><FiCheck size={14} /></div>
              </div>
              <div className="ticket-card-title">{product.name}</div>
            </label>
          );
        })}
      </div>

      <div className="quantity-stepper-row">
        <div>
          <div className="qty-label-eyebrow">ANTALL</div>
          <div className="qty-label-title">Velg antall billetter</div>
        </div>
        <div className="qty-controls">
          <label className="qty-btn minus">
            <HapticSwitch onChange={() => { vibrate(); setQty((q) => Math.max(1, q - 1)); }} />
            <FiMinus size={28} />
          </label>
          <div className="qty-value">{qty}</div>
          <label className="qty-btn plus">
            <HapticSwitch onChange={() => { vibrate(); setQty((q) => Math.min(20, q + 1)); }} />
            <FiPlus size={28} />
          </label>
        </div>
      </div>

      <div className="print-cta-container">
        <label className={`print-button${(!selectedId || printing) ? " disabled" : ""}`}>
          <HapticSwitch disabled={!selectedId || printing} onChange={handlePrint} />
          {printing ? (
            <span className="print-dots"><span></span><span></span><span></span></span>
          ) : (
            <>
              <FiPrinter size={28} />
              <span>Skriv ut</span>
            </>
          )}
        </label>
      </div>

      <PrintModal printModal={printModal} onClose={() => { clearInterval(countdownRef.current); setPrintModal(null); }} />
    </div>
  );
}
